import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FileText, Download, Loader2, CheckCircle2, ExternalLink, Link as LinkIcon,
} from "lucide-react";
import api, { API } from "@/utils/api";

function fmt(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

async function downloadFile(url, filename) {
  try {
    const token = localStorage.getItem("sendtoo_token");
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) throw new Error();
    const blob = await r.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(objUrl);
  } catch {
    toast.error("Download failed");
  }
}

export default function CombineInvoices() {
  const [searchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(searchParams.get("customer_id") || "");
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  const selectedCustomer = customers.find((c) => c.id === customerId) || null;
  const filteredCustomers = customers.filter((c) => {
    const q = customerSearch.toLowerCase();
    if (!q) return false;
    return (
      c.name?.toLowerCase().includes(q) ||
      c.phone?.includes(q) ||
      c.email?.toLowerCase().includes(q)
    );
  });

  const [shipments, setShipments] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loadingShipments, setLoadingShipments] = useState(false);

  const [combining, setCombining] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/users", { params: { role: "customer" } });
        setCustomers(r.data || []);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadShipments = useCallback(async (custId) => {
    if (!custId) {
      setShipments([]);
      return;
    }
    setLoadingShipments(true);
    setSelected(new Set());
    try {
      const r = await api.get("/shipments", { params: { customer_id: custId, limit: 500 } });
      setShipments(r.data.items || []);
    } catch {
      setShipments([]);
      toast.error("Could not load shipments for this customer");
    } finally {
      setLoadingShipments(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadShipments(customerId);
  }, [customerId, loadShipments]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === shipments.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(shipments.map((s) => s.id || s._id)));
    }
  };

  const handleCombine = async () => {
    if (selected.size < 2) {
      toast.error("Select at least 2 shipments to combine");
      return;
    }
    setCombining(true);
    try {
      const r = await api.post("/v2/invoices/combine", {
        customer_id: customerId,
        shipment_ids: Array.from(selected),
      });
      const data = r.data;
      setResult(data);
      toast.success(
        data.invoice_numbers.length > 1
          ? `Combined into 2 tax invoices under ${data.combined_ref}`
          : `Combined into ${data.invoice_numbers[0]}`
      );
      loadShipments(customerId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Combine failed — check connection and try again");
    } finally {
      setCombining(false);
    }
  };

  const selectedCount = selected.size;

  return (
    <DashboardLayout>
      <div className="space-y-4 p-1">
        <div>
          <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <FileText className="w-5 h-5" /> Combine Invoices
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Pick a customer, select any mix of their shipments (already invoiced or not), and merge
            them into one invoice with a single payment link for whatever's still unpaid.
          </p>
        </div>

        <div className="bg-white border rounded-lg p-3">
          <span className="text-xs font-medium text-slate-600 whitespace-nowrap">Customer:</span>
          <div className="relative mt-1.5 max-w-md">
            <input
              value={selectedCustomer ? "" : customerSearch}
              onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
              onFocus={() => setShowCustomerDropdown(true)}
              placeholder="Search by name, phone, or email…"
              className="w-full border border-slate-300 rounded px-3 py-1.5 text-sm"
              data-testid="combine-customer-search"
            />

            {selectedCustomer && (
              <div className="mt-2 bg-brand-50 border border-brand-100 rounded-sm p-2 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-brand-700">{selectedCustomer.name}</p>
                  <p className="text-xs text-slate-400">{selectedCustomer.email}{selectedCustomer.phone && ` • ${selectedCustomer.phone}`}</p>
                </div>
                <button
                  onClick={() => { setCustomerId(""); setCustomerSearch(""); }}
                  className="text-slate-400 hover:text-red-500 text-xs font-medium"
                  data-testid="combine-customer-clear"
                >
                  Change
                </button>
              </div>
            )}

            {showCustomerDropdown && !selectedCustomer && filteredCustomers.length > 0 && (
              <div className="absolute z-20 top-full left-0 right-0 bg-white border border-slate-200 rounded-sm shadow-lg mt-1 max-h-64 overflow-y-auto">
                {filteredCustomers.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => { setCustomerId(c.id); setCustomerSearch(""); setShowCustomerDropdown(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-brand-50 text-left"
                    data-testid={`combine-customer-option-${c.id}`}
                  >
                    <div className="w-7 h-7 rounded-full bg-brand-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {c.name?.[0]}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{c.name}</p>
                      <p className="text-xs text-slate-400 truncate">{c.email}{c.phone && ` • ${c.phone}`}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {showCustomerDropdown && !selectedCustomer && customerSearch && filteredCustomers.length === 0 && (
              <div className="absolute z-20 top-full left-0 right-0 bg-white border border-slate-200 rounded-sm shadow-lg mt-1 p-4 text-center">
                <p className="text-sm text-slate-500">No matching customer</p>
              </div>
            )}
          </div>
        </div>

        {customerId && (
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
              <div className="text-sm font-medium text-slate-700">
                {loadingShipments
                  ? "Loading shipments…"
                  : `${shipments.length} shipment${shipments.length === 1 ? "" : "s"} — ${selectedCount} selected`}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={toggleAll} disabled={!shipments.length}>
                  {selected.size === shipments.length && shipments.length > 0 ? "Clear All" : "Select All"}
                </Button>
                <Button
                  size="sm"
                  onClick={handleCombine}
                  disabled={selectedCount < 2 || combining}
                  data-testid="combine-action-btn"
                >
                  {combining ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                  Combine into 1 Invoice
                </Button>
              </div>
            </div>

            {loadingShipments ? (
              <div className="p-8 text-center text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…
              </div>
            ) : shipments.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">No shipments found for this customer.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b bg-slate-50">
                    <th className="px-4 py-2 w-8"></th>
                    <th className="px-4 py-2">Order ID</th>
                    <th className="px-4 py-2">AWB / LR</th>
                    <th className="px-4 py-2">Date</th>
                    <th className="px-4 py-2">Amount</th>
                    <th className="px-4 py-2">Payment</th>
                    <th className="px-4 py-2">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {shipments.map((s) => {
                    const id = s.id || s._id;
                    const isPaid = s.payment_status === "paid";
                    return (
                      <tr key={id} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-2">
                          <input
                            type="checkbox"
                            checked={selected.has(id)}
                            onChange={() => toggle(id)}
                          />
                        </td>
                        <td className="px-4 py-2 font-medium">{s.order_id}</td>
                        <td className="px-4 py-2 text-slate-500">{s.lr_number || s.tracking_id || "—"}</td>
                        <td className="px-4 py-2 text-slate-500">
                          {s.created_at ? new Date(s.created_at).toLocaleDateString("en-IN") : "—"}
                        </td>
                        <td className="px-4 py-2">{fmt(s.bill_amount_override || s.price)}</td>
                        <td className="px-4 py-2">
                          <Badge variant={isPaid ? "default" : "secondary"} className={isPaid ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                            {isPaid ? "PAID" : (s.payment_status || "PENDING").toUpperCase()}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 text-slate-500">
                          {s.invoice_id ? (
                            <span className="inline-flex items-center gap-1 text-xs">
                              <CheckCircle2 className="w-3.5 h-3.5 text-blue-500" /> Invoiced
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">Not invoiced</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <Dialog open={!!result} onOpenChange={(open) => !open && setResult(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Combined Successfully</DialogTitle>
          </DialogHeader>
          {result && (
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-slate-500">Combined Reference:</span>{" "}
                <span className="font-medium">{result.combined_ref}</span>
              </div>
              <div>
                <span className="text-slate-500">
                  {result.invoice_numbers.length > 1 ? "Tax Invoices Generated:" : "Invoice Number:"}
                </span>
                <ul className="mt-1 space-y-1">
                  {result.invoice_numbers.map((num, i) => (
                    <li key={num} className="flex items-center justify-between border rounded px-3 py-1.5">
                      <span className="font-medium">{num}</span>
                      <button
                        onClick={() => downloadFile(`${API}/v2/invoices/${result.invoice_ids[i]}/pdf`, `Sendtoo_${num}.pdf`)}
                        className="text-blue-600 text-xs flex items-center gap-1 hover:underline"
                      >
                        <Download className="w-3.5 h-3.5" /> PDF
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center justify-between border rounded px-3 py-1.5 bg-slate-50">
                <span>Excel Export (all shipments)</span>
                <button
                  onClick={() => downloadFile(`${API}/v2/invoices/combined/${result.combined_ref}/export`, `Sendtoo_${result.combined_ref}.xlsx`)}
                  className="text-blue-600 text-xs flex items-center gap-1 hover:underline"
                >
                  <Download className="w-3.5 h-3.5" /> Download
                </button>
              </div>
              {result.unpaid_total > 0 ? (
                <div className="border rounded px-3 py-2 bg-amber-50">
                  <div className="text-xs text-slate-600 mb-1">
                    Unpaid total: <span className="font-medium">{fmt(result.unpaid_total)}</span>
                  </div>
                  {result.payment_link_url && (
                    <a
                      href={result.payment_link_url}
                      target="_blank" rel="noreferrer"
                      className="text-blue-600 text-xs flex items-center gap-1 hover:underline"
                    >
                      <LinkIcon className="w-3.5 h-3.5" /> Payment Link <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              ) : (
                <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">
                  All selected shipments were already paid — no payment link needed.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setResult(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
